// src/components/comparison/ComparisonList.tsx

import React, { useState, useMemo } from 'react';
import { List, Button, Badge } from 'antd';
import { ComparisonResult } from '../../types';
import {
    averageColorBySimilarity,
    colorBySimilarity,
    textColorBySimilarity
} from "../../services/utils/colorUtils";
import { useSimSettings } from '../../hooks/useSimSettings';

type SortOrder = 'asc' | 'desc' | null;

/**
 * Пропы для универсального компонента ComparisonList.
 */
interface ComparisonListProps {
    /** Массив результатов сравнения (подразумевается, что similarity — это [simA, simB]). */
    data: ComparisonResult[];

    /**
     * Если надо переопределить рендер одного элемента списка:
     *  renderItem(item, index) → ReactNode
     *
     * По умолчанию используется «три бэйджа + текст propB».
     */
    renderItem?: (item: ComparisonResult, index: number) => React.ReactNode;

    /** Начальный порядок сортировки: 'asc' | 'desc' | null (по умолчанию null). */
    initialSortOrder?: SortOrder;

    /**
     * Показывать ли среднее по всей выборке вверху (необязательный функционал).
     * По умолчанию — true.
     */
    showGlobalAverage?: boolean;
}

const ComparisonList: React.FC<ComparisonListProps> = ({
                                                           data,
                                                           renderItem,
                                                           initialSortOrder = null,
                                                           showGlobalAverage = true,
                                                       }) => {
    const [sortOrder, setSortOrder] = useState<SortOrder>(initialSortOrder);
    const { similarityDisplayMode, calcSim } = useSimSettings();

    /**
     * Переключение порядка сортировки:
     *  - null → 'asc' → 'desc' → null → ...
     */
    const toggleSortOrder = () => {
        setSortOrder((prev) => {
            if (prev === null) return 'asc';
            if (prev === 'asc') return 'desc';
            return null;
        });
    };

    /**
     * Функция, возвращающая массив ComparisonResult в соответствии с текущей сортировкой.
     */
    const sortedData = useMemo(() => {
        if (!sortOrder) {
            // Без сортировки просто возвращаем исходные данные
            return data;
        }
        return [...data].sort((a, b) => {
            const avgA = calcSim(a.similarity);
            const avgB = calcSim(b.similarity);
            return sortOrder === 'asc' ? avgA - avgB : avgB - avgA;
        });
    }, [data, sortOrder]);

    /**
     * Подсчёт «глобальной» средней схожести по всему массиву.
     */
    const globalAverage = useMemo(() => {
        if (!data.length) return 0;
        let sum = 0;
        data.forEach((item) => { sum += calcSim(item.similarity); });
        return sum / data.length;
    }, [data, calcSim]);

    /**
     * Дефолтный метод рендера одного элемента (если renderItem не передан).
     * Показывает три «бэйджа»:
     *  - simA
     *  - avg(simA, simB)
     *  - simB
     * Плюс текст свойства propB (или что-то ещё).
     */
    const defaultRenderItem = (item: ComparisonResult): React.ReactNode => {
        const [simA, simB] = item.similarity;
        const avg = calcSim(item.similarity);

        return (
            <div
                style={{
                    backgroundColor: averageColorBySimilarity(simA, simB),
                    borderRadius: '6px',
                    marginBottom: '8px',
                    padding: '6px',
                    display: 'flex',
                    alignItems: 'center',
                }}
            >
                {similarityDisplayMode === 'triplet' && (
                    <Badge
                        count={`${Math.round(simA * 100) / 100}`}
                        style={{
                            backgroundColor: colorBySimilarity(simA),
                            color: textColorBySimilarity(simA),
                            marginRight: 8,
                        }}
                    />
                )}

                <Badge
                    count={`${Math.round(avg * 100) / 100}`}
                    style={{
                        backgroundColor: averageColorBySimilarity(simA, simB),
                        color: textColorBySimilarity(avg),
                        marginRight: 8,
                    }}
                />

                {similarityDisplayMode === 'triplet' && (
                    <Badge
                        count={`${Math.round(simB * 100) / 100}`}
                        style={{
                            backgroundColor: colorBySimilarity(simB),
                            color: textColorBySimilarity(simB),
                            marginRight: 8,
                        }}
                    />
                )}

                <div style={{ marginLeft: '8px' }}>
                    {item.propB?.text}
                </div>
            </div>
        );
    };

    return (
        <div className="p-4 bg-white rounded-lg shadow">
            {/* Кнопка переключения порядка сортировки */}
            <Button onClick={toggleSortOrder} style={{ marginBottom: '10px' }}>
                {sortOrder === null
                    ? 'Сортировать: (нет) → по возрастанию'
                    : sortOrder === 'asc'
                        ? 'Сортировать: по возрастанию → убывание'
                        : 'Сортировать: по убыванию → (нет)'
                }
            </Button>

            {/* Общая информация о списке (если showGlobalAverage) */}
            {showGlobalAverage && (
                <div style={{ marginBottom: '10px', fontWeight: 'bold' }}>
                    Всего сравнений: {data.length}. Среднее сходство: {Math.round(globalAverage * 100)/100}
                </div>
            )}

            {/* Сам список */}
            <List
                dataSource={sortedData}
                renderItem={(item, index) => (
                    <List.Item style={{ border: 'none', padding: 0 }}>
                        {renderItem ? renderItem(item, index) : defaultRenderItem(item)}
                    </List.Item>
                )}
            />
        </div>
    );
};

export default ComparisonList;
